import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api.js'
import { useAuth } from '../../hooks/useAuth.js'
import { ago, dateTime, money, percent } from '../../lib/format.js'
import { Badge, Card, Empty, PageHead, Pager, RowsSkeleton, Stat, StatsSkeleton, Tabs } from '../../components/ui.js'
import { ConfirmDialog, toast } from '../../components/feedback.js'
import { CartIcon, CheckCircleIcon, DownloadIcon, MailIcon, RupeeIcon, StoreIcon, TargetIcon, WhatsAppIcon, XIcon } from '../../components/icons.js'
import { FilterBar, filtersFromParams, filtersKey, filtersToParams } from './FilterBar.js'
import { Composer } from './Composer.js'
import { CustomerDrawer } from './CustomerDrawer.js'
import { CampaignsPanel } from './Campaigns.js'
import { AutomationPanel } from './Automation.js'
import { AnalyticsPanel } from './Analytics.js'
import { SEGMENT_LABEL, downloadCsv, newIdempotencyKey, type Channel, type Filters, type ListPayload, type RecoverableRow, type SegmentKey, type Selection, type SortKey } from './types.js'

/**
 * Abandoned checkouts: find them, pick them, reach them, and see what came back.
 *
 * Filters, sort and page live in the URL, so a view can be bookmarked and the
 * back button works. Selection never leaves the server's hands for large sets:
 * ticking rows keeps their ids (across pages); "select all N matching" keeps
 * the filters plus whatever is unticked afterwards. Changing the filters clears
 * the selection — a selection that silently meant something new would be the
 * worst kind of surprise before a bulk send.
 */

type Tab = 'checkouts' | 'campaigns' | 'automation' | 'analytics'

type SelectionState = { mode: 'ids'; ids: Set<string> } | { mode: 'filter'; excluded: Set<string> }

const EMPTY: SelectionState = { mode: 'ids', ids: new Set() }

const SORTABLE: Array<{ key: SortKey; label: string; className?: string }> = [
  { key: 'name', label: 'Shopper' },
  { key: 'at', label: 'Abandoned' },
  { key: 'value', label: 'Cart value', className: 'right' },
  { key: 'reminders', label: 'Reminders' },
  { key: 'clicks', label: 'Link clicked' },
]

function SortHeader({ column, sort, dir, onSort }: { column: (typeof SORTABLE)[number]; sort: SortKey; dir: 'asc' | 'desc'; onSort: (key: SortKey) => void }): ReactNode {
  const active = sort === column.key
  return (
    <th className={column.className} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" className={active ? 'th-sort is-active' : 'th-sort'} onClick={() => onSort(column.key)}>
        {column.label} <span aria-hidden>{active ? (dir === 'asc' ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  )
}

export function AbandonedCheckoutsPage(): ReactNode {
  const { shop } = useAuth()
  const queryClient = useQueryClient()
  const [params, setParams] = useSearchParams()

  const tab = (params.get('tab') as Tab | null) ?? 'checkouts'
  const filters = useMemo(() => filtersFromParams(params), [params])
  const key = filtersKey(filters)
  const sort = (params.get('sort') as SortKey | null) ?? 'at'
  const dir = params.get('dir') === 'asc' ? 'asc' : 'desc'
  const page = Math.max(1, Number(params.get('page') ?? 1) || 1)
  const perPage = [20, 50, 100].includes(Number(params.get('perPage'))) ? Number(params.get('perPage')) : 20

  const [selection, setSelection] = useState<SelectionState>(EMPTY)
  const [composer, setComposer] = useState<{ channel: Channel } | null>(null)
  const [drawer, setDrawer] = useState<string | null>(null)
  const [rowRemind, setRowRemind] = useState<{ row: RecoverableRow; channel: Channel } | null>(null)

  const patch = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          for (const [name, value] of Object.entries(changes)) {
            if (value === null || value === '') next.delete(name)
            else next.set(name, value)
          }
          return next
        },
        { replace: true },
      )
    },
    [setParams],
  )

  const applyFilters = useCallback(
    (next: Filters) =>
      setParams((current) => {
        const out = new URLSearchParams(current)
        filtersToParams(next, out)
        out.delete('page')
        return out
      }),
    [setParams],
  )

  // Filters changed: the old selection no longer means what it meant.
  const previousKey = useRef(key)
  const selectionRef = useRef(selection)
  selectionRef.current = selection
  useEffect(() => {
    if (previousKey.current === key) return
    previousKey.current = key
    const current = selectionRef.current
    if (current.mode === 'filter' || current.ids.size > 0) {
      toast('info', 'Selection cleared', 'The filters changed, so the previous selection was cleared.')
      setSelection(EMPTY)
    }
  }, [key])

  const list = useQuery({
    queryKey: ['abandoned', shop?.id, key, sort, dir, page, perPage],
    queryFn: () => api<ListPayload>('/v1/abandoned', { query: { ...(filters as unknown as Record<string, string | number | undefined>), sort, dir, page, perPage } }),
    // Runs on every tab: it is also what says whether this viewer may act.
    enabled: Boolean(shop?.id),
    placeholderData: (previous) => previous,
  })

  const segments = useQuery({
    queryKey: ['abandoned-segments', shop?.id],
    queryFn: () => api<{ segments: Array<{ key: SegmentKey; count: number; rule: string }> }>('/v1/abandoned/segments'),
    enabled: Boolean(shop?.id) && tab === 'checkouts',
    staleTime: 30_000,
  })

  const data = list.data
  const total = data?.total ?? 0
  const rows = data?.items ?? []
  const canWrite = data?.access.canWrite ?? false
  const filteringAllStores = filters.store === 'all'

  const selectedCount = selection.mode === 'ids' ? selection.ids.size : Math.max(0, total - selection.excluded.size)
  const isSelected = (id: string): boolean => (selection.mode === 'ids' ? selection.ids.has(id) : !selection.excluded.has(id))
  const pageAllSelected = rows.length > 0 && rows.every((row) => isSelected(row.id))
  const pageSomeSelected = rows.some((row) => isSelected(row.id))

  const toggleRow = (id: string): void =>
    setSelection((current) => {
      if (current.mode === 'ids') {
        const ids = new Set(current.ids)
        if (ids.has(id)) ids.delete(id)
        else ids.add(id)
        return { mode: 'ids', ids }
      }
      const excluded = new Set(current.excluded)
      if (excluded.has(id)) excluded.delete(id)
      else excluded.add(id)
      return { mode: 'filter', excluded }
    })

  const togglePage = (): void =>
    setSelection((current) => {
      if (current.mode === 'filter') {
        const excluded = new Set(current.excluded)
        for (const row of rows) {
          if (pageAllSelected) excluded.add(row.id)
          else excluded.delete(row.id)
        }
        return { mode: 'filter', excluded }
      }
      const ids = new Set(current.ids)
      for (const row of rows) {
        if (pageAllSelected) ids.delete(row.id)
        else ids.add(row.id)
      }
      return { mode: 'ids', ids }
    })

  const serverSelection: Selection | null =
    selectedCount === 0 ? null : selection.mode === 'ids' ? { mode: 'ids', ids: [...selection.ids] } : { mode: 'filter', filters, excludeIds: [...selection.excluded] }

  const exportCsv = useMutation({
    mutationFn: (scope: 'filter' | 'selection') =>
      scope === 'selection' && serverSelection
        ? api<{ filename: string; csv: string; count: number }>('/v1/abandoned/export', { method: 'POST', body: { selection: serverSelection } })
        : api<{ filename: string; csv: string; count: number }>('/v1/abandoned/export', { query: filters as unknown as Record<string, string | number | undefined> }),
    onSuccess: (result) => {
      downloadCsv(result.filename, result.csv)
      toast('ok', 'Export ready', `${result.count.toLocaleString('en-IN')} checkouts downloaded.`)
    },
    onError: (error) => toast('error', 'Export failed', (error as Error).message),
  })

  const remindRow = useMutation({
    mutationFn: (input: { row: RecoverableRow; channel: Channel }) =>
      api<{ status: string; charged: string }>(`/v1/abandoned/${input.row.id}/remind`, { method: 'POST', body: { channel: input.channel, idempotencyKey: newIdempotencyKey('manual') } }),
    onSuccess: (result, input) => {
      setRowRemind(null)
      const label = input.channel === 'whatsapp' ? 'WhatsApp' : 'Email'
      if (result.status === 'sent') toast('ok', `${label} sent`, `To ${input.row.customerName ?? 'the shopper'}${Number(result.charged) > 0 ? ` · ${money(result.charged)} charged` : ''}.`)
      else toast('info', `${label} queued`, 'It will go out shortly.')
      void queryClient.invalidateQueries({ queryKey: ['abandoned'] })
    },
    onError: (error) => {
      setRowRemind(null)
      toast('error', 'Reminder not sent', (error as Error).message)
    },
  })

  if (!shop) {
    return (
      <Card>
        <Empty icon={<StoreIcon size={24} />} title="No store selected">
          Abandoned checkouts belong to a store. Choose one from the switcher above.
        </Empty>
      </Card>
    )
  }

  const summary = data?.summary
  const pageCount = Math.max(1, Math.ceil(total / perPage))

  return (
    <>
      <PageHead
        title="Abandoned checkouts"
        subtitle="Shoppers who started a checkout and left — and what you can still recover."
        crumbs={[{ label: 'Engagement' }, { label: 'Abandoned checkouts' }]}
      />

      {tab === 'checkouts' && list.isPending && <StatsSkeleton count={4} />}
      {tab === 'checkouts' && summary && (
        <section className="grid grid--4">
          <Stat label="Still recoverable" value={summary.openCount.toLocaleString('en-IN')} icon={<CartIcon size={20} />} tone="var(--bad)" />
          <Stat label="Cart value at stake" value={money(summary.openValue)} icon={<RupeeIcon size={20} />} tone="var(--warn)" />
          <Stat label="Recovered" value={summary.recoveredCount.toLocaleString('en-IN')} icon={<CheckCircleIcon size={20} />} tone="var(--ok)" footnote={<span className="faint">worth {money(summary.recoveredValue)}</span>} />
          <Stat label="Recovery rate" value={summary.recoveryRate === null ? '—' : percent(summary.recoveryRate)} icon={<TargetIcon size={20} />} tone="var(--brand)" footnote={<span className="faint">of everything recoverable</span>} />
        </section>
      )}

      <Card flush>
        <Tabs
          value={tab}
          onChange={(next) => patch({ tab: next === 'checkouts' ? null : next })}
          tabs={[
            { key: 'checkouts', label: 'Checkouts', count: tab === 'checkouts' ? summary?.openCount : undefined },
            { key: 'campaigns', label: 'Campaigns' },
            { key: 'automation', label: 'Automation' },
            { key: 'analytics', label: 'Analytics' },
          ]}
        />

        {tab === 'checkouts' && (
          <>
            {segments.data && (
              <div className="segments" role="group" aria-label="Smart segments">
                {segments.data.segments.map((segment) => (
                  <button
                    key={segment.key}
                    type="button"
                    className={filters.segment === segment.key ? 'segment is-active' : 'segment'}
                    aria-pressed={filters.segment === segment.key}
                    title={segment.rule}
                    onClick={() => applyFilters({ ...filters, segment: filters.segment === segment.key ? undefined : segment.key, recovery: filters.recovery })}
                  >
                    {SEGMENT_LABEL[segment.key]}
                    <span className="segment__count">{segment.count.toLocaleString('en-IN')}</span>
                  </button>
                ))}
              </div>
            )}

            <FilterBar
              filters={filters}
              total={data ? total : null}
              multiStore={data?.access.multiStore ?? false}
              canWrite={canWrite}
              onChange={applyFilters}
              onExport={() => exportCsv.mutate('filter')}
              exporting={exportCsv.isPending}
            />

            {selectedCount > 0 && (
              <div className="bulkbar" role="region" aria-label="Bulk actions">
                <strong>{selectedCount.toLocaleString('en-IN')} selected</strong>
                {selection.mode === 'ids' && pageAllSelected && total > selection.ids.size && (
                  <button type="button" className="link-btn" onClick={() => setSelection({ mode: 'filter', excluded: new Set() })}>
                    Select all {total.toLocaleString('en-IN')} matching
                  </button>
                )}
                {selection.mode === 'filter' && <span className="muted small">All matching the current filters{selection.excluded.size > 0 ? `, minus ${selection.excluded.size} unticked` : ''}</span>}
                <span className="spacer" />
                {canWrite && (
                  <>
                    <button
                      type="button"
                      className="btn btn--sm"
                      disabled={filteringAllStores}
                      title={filteringAllStores ? 'Switch to a single store to send' : 'Preview and send a WhatsApp campaign'}
                      onClick={() => setComposer({ channel: 'whatsapp' })}
                    >
                      <WhatsAppIcon size={14} /> WhatsApp
                    </button>
                    <button type="button" className="btn btn--sm" disabled={filteringAllStores} title={filteringAllStores ? 'Switch to a single store to send' : 'Preview and send an email campaign'} onClick={() => setComposer({ channel: 'email' })}>
                      <MailIcon size={14} /> Email
                    </button>
                    <button type="button" className="btn btn--sm" disabled={exportCsv.isPending || filteringAllStores} onClick={() => exportCsv.mutate('selection')}>
                      <DownloadIcon size={14} /> Export selected
                    </button>
                  </>
                )}
                <button type="button" className="icon-btn" aria-label="Clear selection" onClick={() => setSelection(EMPTY)}>
                  <XIcon size={16} />
                </button>
              </div>
            )}

            {list.error && (
              <Empty error title="Could not load abandoned checkouts" action={<button type="button" className="btn btn--sm" onClick={() => void list.refetch()}>Try again</button>}>
                {(list.error as Error).message}
              </Empty>
            )}

            {list.isPending && <RowsSkeleton rows={8} />}

            {data && rows.length === 0 && !list.error && (
              <Empty icon={<CartIcon size={24} />} title={key === filtersKey({ recovery: 'open' }) ? 'Nothing to recover' : 'No checkouts match'}>
                {key === filtersKey({ recovery: 'open' }) ? 'Every checkout was completed. That is a good problem to have.' : 'Try removing a filter or two.'}
              </Empty>
            )}

            {data && rows.length > 0 && (
              <>
                <div className={list.isFetching ? 'table-wrap table-wrap--sticky is-loading' : 'table-wrap table-wrap--sticky'}>
                  <table className="table table--select">
                    <thead>
                      <tr>
                        <th className="col-check">
                          <input
                            type="checkbox"
                            aria-label="Select all checkouts on this page"
                            checked={pageAllSelected}
                            ref={(element) => {
                              if (element) element.indeterminate = !pageAllSelected && pageSomeSelected
                            }}
                            onChange={togglePage}
                          />
                        </th>
                        {SORTABLE.slice(0, 2).map((column) => (
                          <SortHeader key={column.key} column={column} sort={sort} dir={dir} onSort={(next) => patch({ sort: next, dir: sort === next && dir === 'desc' ? 'asc' : 'desc', page: null })} />
                        ))}
                        <th className="hide-sm">Stage</th>
                        <SortHeader column={SORTABLE[2]!} sort={sort} dir={dir} onSort={(next) => patch({ sort: next, dir: sort === next && dir === 'desc' ? 'asc' : 'desc', page: null })} />
                        <th className="hide-sm">Contact</th>
                        <SortHeader column={SORTABLE[3]!} sort={sort} dir={dir} onSort={(next) => patch({ sort: next, dir: sort === next && dir === 'desc' ? 'asc' : 'desc', page: null })} />
                        <th className="right">Remind</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr key={row.id} className={['is-clickable', isSelected(row.id) ? 'is-selected' : ''].join(' ')} onClick={() => setDrawer(row.id)}>
                          <td className="col-check" onClick={(event) => event.stopPropagation()}>
                            <input type="checkbox" aria-label={`Select ${row.customerName ?? 'guest shopper'}`} checked={isSelected(row.id)} onChange={() => toggleRow(row.id)} />
                          </td>
                          <td>
                            <button type="button" className="link-btn link-btn--strong" onClick={(event) => { event.stopPropagation(); setDrawer(row.id) }}>
                              {row.customerName ?? 'Guest shopper'}
                            </button>
                            <span className="table__sub">
                              {row.phone ?? 'no phone'}
                              {row.returning ? ' · returning' : ''}
                              {filteringAllStores && row.store ? ` · ${row.store}` : ''}
                            </span>
                          </td>
                          <td>
                            {ago(row.at)}
                            <span className="table__sub">{dateTime(row.at).split(',')[0]}</span>
                          </td>
                          <td className="hide-sm">
                            <Badge tone={row.recovered ? 'ok' : row.stage === 'Payment' ? 'warn' : row.stage === 'OTP' ? 'bad' : 'info'}>{row.recovered ? 'Recovered' : row.stage}</Badge>
                            {row.paymentStatus === 'failed' && <span className="table__sub">payment failed</span>}
                          </td>
                          <td className="right num">
                            {money(row.value)}
                            <span className="table__sub" title={row.product ?? undefined}>
                              {row.items} item{row.items === 1 ? '' : 's'}
                            </span>
                          </td>
                          <td className="hide-sm">
                            <span className="row" style={{ gap: 4 }}>
                              <Badge tone={row.whatsappEligible ? 'ok' : undefined}>WA</Badge>
                              <Badge tone={row.emailEligible ? 'ok' : undefined}>Email</Badge>
                              {row.optedOut && <Badge tone="warn">Opted out</Badge>}
                            </span>
                          </td>
                          <td>
                            {row.reminded > 0 ? `${row.reminded} sent` : <span className="faint">None</span>}
                            <span className="table__sub">{row.clickedAt ? `clicked ${ago(row.clickedAt)}${row.clicks > 1 ? ` · ${row.clicks}×` : ''}` : row.reminded > 0 ? 'not clicked yet' : ''}</span>
                          </td>
                          <td className="right" onClick={(event) => event.stopPropagation()}>
                            {!row.recovered && canWrite && (
                              <span className="row-actions">
                                <button type="button" className="btn btn--sm btn--icon" disabled={!row.whatsappEligible} aria-label={`Send WhatsApp to ${row.customerName ?? 'shopper'}`} title={row.whatsappEligible ? 'Send the WhatsApp reminder now' : 'Not eligible for WhatsApp — open for details'} onClick={() => setRowRemind({ row, channel: 'whatsapp' })}>
                                  <WhatsAppIcon size={14} />
                                </button>
                                <button type="button" className="btn btn--sm btn--icon" disabled={!row.emailEligible} aria-label={`Send email to ${row.customerName ?? 'shopper'}`} title={row.emailEligible ? 'Send the email reminder now' : 'Not eligible for email — open for details'} onClick={() => setRowRemind({ row, channel: 'email' })}>
                                  <MailIcon size={14} />
                                </button>
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pager
                  page={page}
                  pageCount={pageCount}
                  total={total}
                  perPage={perPage}
                  sizes={[20, 50, 100]}
                  onPage={(next) => patch({ page: next === 1 ? null : String(next) })}
                  onPerPage={(next) => patch({ perPage: next === 20 ? null : String(next), page: null })}
                  noun="checkouts"
                />
              </>
            )}
          </>
        )}
      </Card>

      {tab === 'campaigns' && <CampaignsPanel canWrite={canWrite} />}
      {tab === 'automation' && <AutomationPanel canWrite={canWrite} />}
      {tab === 'analytics' && <AnalyticsPanel shopId={shop.id} multiStore={data?.access.multiStore ?? false} />}

      <Composer
        open={composer !== null}
        initialChannel={composer?.channel ?? 'whatsapp'}
        selection={serverSelection}
        selectedCount={selectedCount}
        onClose={() => setComposer(null)}
        onCreated={() => {
          setComposer(null)
          setSelection(EMPTY)
          void queryClient.invalidateQueries({ queryKey: ['abandoned'] })
          void queryClient.invalidateQueries({ queryKey: ['abandoned-campaigns'] })
          patch({ tab: 'campaigns' })
        }}
      />

      <CustomerDrawer id={drawer} onClose={() => setDrawer(null)} />

      <ConfirmDialog
        open={rowRemind !== null}
        title={rowRemind?.channel === 'whatsapp' ? 'Send a WhatsApp reminder?' : 'Send an email reminder?'}
        confirmLabel="Send now"
        pending={remindRow.isPending}
        onClose={() => setRowRemind(null)}
        onConfirm={() => rowRemind && remindRow.mutate(rowRemind)}
        message={
          rowRemind
            ? `${rowRemind.row.customerName ?? 'This shopper'} will get your ${rowRemind.channel === 'whatsapp' ? 'abandoned-checkout WhatsApp template, charged per message from your wallet' : 'reminder email (free)'}.`
            : ''
        }
      />
    </>
  )
}
